import React from 'react';
import validator from 'validator';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import EmailRepeater from '../components/EmailRepeater.jsx';
import ContentContainer from '../layout/ContentContainer.jsx';
import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import { getTextDefaults } from './translations/contractorDispatcherRegistrationTranslations.js';

const ContractorDispatcherRegistrationSection = ({
	name = 'groupOwnerEmails',
	values = {},
	onChange,
	navigationProps,
	selectedLanguage = 'EN',
	disabled = false,
	title,
	description,
	textOverride = {},
}) => {
	// Keep list structure for future extensibility, but UI will limit to single value
	const emailValues = values[name] || [];
	let groupName = values?.userData?.groups?.[0]?.name;
	const canContinue = () => {
		if (disabled) {
			return false;
		}

		if (!emailValues || emailValues.length === 0) {
			return false;
		}

		const allEmailsValid = emailValues.every((email) => {
			if (!email || email.trim() === '') {
				return false;
			}
			return validator.isEmail(email);
		});

		return allEmailsValid;
	};

	const handleChange = ({ target }) => {
		onChange({ target: { name, value: target.value } });
	};

	const defaultText = getTextDefaults(selectedLanguage);
	const text = { ...defaultText, ...textOverride };

	return (
		<SectionContainer
			Navigation={<SectionNav {...navigationProps} canContinue={canContinue()} selectedLanguage={selectedLanguage} />}
		>
			<ContentContainer>
				<Typography variant="h5" component="h2" sx={{ fontWeight: 600, marginBottom: 2 }}>
					{title || text.title}: {groupName || ''}
				</Typography>

				<Box sx={{ marginBottom: 2 }} dangerouslySetInnerHTML={{ __html: description || text.description }} />
				<Alert severity="info">{text.securityNote}</Alert>
				<EmailRepeater
					name={name}
					value={emailValues}
					onChange={handleChange}
					disabled={disabled}
					label={text.emailLabel}
					placeholder={text.emailPlaceholder}
					addButtonText={text.addButtonText}
					maxItems={1}
				/>
			</ContentContainer>
		</SectionContainer>
	);
};

ContractorDispatcherRegistrationSection.type = 'ContractorDispatcherRegistrationSection';
ContractorDispatcherRegistrationSection.hasNavigation = true;

ContractorDispatcherRegistrationSection.spec = [
	{
		name: 'ContractorDispatcherRegistrationSection',
		key: 'ContractorDispatcherRegistrationSection',
		title: 'Contractor Admin Registration',
		translatable: ['title', 'description'],
		fields: [
			{
				type: 'TextField',
				name: 'title',
				label: 'Title',
			},
			{
				type: 'TextArea',
				name: 'description',
				label: 'Description (HTML)',
			},
		],
		defaultProperties: {
			title: 'Contractor Admin Registration',
			name: 'groupOwnerEmails',
			description: `<p>Through this registration form, you can register a Contractor Admin for your company.</p>
			<p>Once the Contractor Admin has been submitted through this form, they will receive an invitation mail to activate their account.</p>`,
			navigation: {
				previousLabel: 'Previous',
				nextLabel: 'Next',
				submitLabel: 'Submit',
			},
			refDataMap: {
				'userData.groups': 'user.groups',
			},
		},
	},
];

export default ContractorDispatcherRegistrationSection;
